import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getAnnounceDetails } from 'services/announce'

const PAGE_SIZE = 5

// Raw status enums come from the announce microservice (Sms.Status /
// BaleMessage.Status); localize for display.
const SMS_STATUS: { [k: string]: string } = {
    IN_PROGRESS: 'در حال ارسال',
    TO_MOKHABERAT: 'تحویل به مخابرات',
    DELIVERED: 'ارسال موفق',
    UNDELIVERED: 'تحویل نشد',
    FAILED: 'ناموفق',
    LAGHV: 'لغو شده',
    BLOCKED: 'مسدود',
}
const BALE_STATUS: { [k: string]: string } = {
    pending: 'در انتظار',
    delivered: 'تحویل شد',
    unsuccessful: 'ناموفق',
}

function ChannelRow({
    label,
    status,
    cost,
}: {
    label: string
    status: string
    cost: number
}) {
    return (
        <div className="flex items-center justify-between py-1 text-sm">
            <span className="text-gray-500">
                {label} — {status}
            </span>
            <span className="text-text-body">
                {cost ? `${cost.toLocaleString()} ریال` : '—'}
            </span>
        </div>
    )
}

function ReceiverCard({ r }: { r: IAnnounceReceiver }) {
    return (
        <div className="mb-3 rounded-2xl border border-[#EFEFEF] bg-white p-4 shadow-[0_2px_10px_rgba(0,0,0,0.05)]">
            <p className="font-bold text-text-body">
                {r.name || r.phone_number}
            </p>
            {r.name && r.phone_number && (
                <p dir="ltr" className="mb-1 text-left text-xs text-gray-400">
                    {r.phone_number}
                </p>
            )}
            <div className="mt-1 border-t border-[#F2F2F2] pt-1">
                <p className="mb-1 text-xs text-gray-400">وضعیت دریافت:</p>
                {r.sms_message && (
                    <ChannelRow
                        label="پیامک"
                        status={
                            SMS_STATUS[r.sms_message.status] ||
                            r.sms_message.status
                        }
                        cost={r.sms_message.cost}
                    />
                )}
                {r.bale_message && (
                    <ChannelRow
                        label="بله"
                        status={
                            BALE_STATUS[r.bale_message.status] ||
                            r.bale_message.status
                        }
                        cost={r.bale_message.cost}
                    />
                )}
                {!r.sms_message && !r.bale_message && (
                    <p className="text-sm text-gray-400">—</p>
                )}
            </div>
        </div>
    )
}

export default function AnnounceDetail() {
    const { id, announceId } = useParams()
    const [items, setItems] = useState<IAnnounceReceiver[]>([])
    const [page, setPage] = useState<number>(1)
    const [hasMore, setHasMore] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [loadingMore, setLoadingMore] = useState<boolean>(false)

    const loadPage = useCallback(
        async (pageNumber: number, reset: boolean) => {
            if (!id || !announceId) return
            reset ? setLoading(true) : setLoadingMore(true)
            try {
                const res = await getAnnounceDetails(id, announceId, pageNumber)
                const batch = res?.announce_messages || []
                setItems((prev) => (reset ? batch : [...prev, ...batch]))
                setHasMore(batch.length >= PAGE_SIZE)
            } catch {
                if (reset) setItems([])
                setHasMore(false)
            } finally {
                setLoading(false)
                setLoadingMore(false)
            }
        },
        [id, announceId]
    )

    useEffect(() => {
        loadPage(1, true)
    }, [loadPage])

    const onLoadMore = () => {
        const next = page + 1
        setPage(next)
        loadPage(next, false)
    }

    return (
        <div className="flex h-full flex-col bg-white">
            <Header title="جزئیات اطلاع‌رسانی" />
            <div className="grow overflow-y-auto px-3 py-3">
                {loading ? (
                    <DotsLoading />
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center text-gray-400">
                        <p>گیرنده‌ای برای این اطلاع‌رسانی یافت نشد</p>
                    </div>
                ) : (
                    <>
                        {items.map((r, i) => (
                            <ReceiverCard key={`${r.phone_number}-${i}`} r={r} />
                        ))}
                        {hasMore && (
                            <button
                                onClick={onLoadMore}
                                disabled={loadingMore}
                                className="my-3 w-full rounded-xl border border-green-1 py-2.5 text-sm text-green-1 disabled:opacity-60"
                            >
                                {loadingMore
                                    ? 'در حال بارگذاری...'
                                    : 'موارد بیشتر'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
